/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { isEqual, pick } from 'lodash';
import { Fragment, Ref, useState } from 'react';

import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import { Colors, Link, Shadows, Spacer, Text } from '@zapier/design-system';
import { TrialConfirmationModal } from '@zapier/design-system-company';
import { HardshipReliefModal, useActiveOffer } from '@zapier/promotions';

import { PlanFeatureList } from '@zapier/design-system-revenue/src/components/pricing-pages/PricingPageV3/PlanFeatureList';
import { PlanSummary } from '@zapier/design-system-revenue/src/components/pricing-pages/PricingPageV3/PlanSummary';

import {
  useAccountSwitch,
  useDiscounts,
} from '@zapier/design-system-revenue/src/hooks';

import {
  ChangeType,
  Currency,
  PlanStack,
  PlanV2,
  Size,
  TooltipPosition,
  TrialType,
} from '@zapier/design-system-revenue/src/types';

import {
  getChangePlanType,
  getTrialUrl,
  isFree,
  isYearly,
  useEligibility,
} from '@zapier/design-system-revenue/src/utils';

import { IrreversiblePlanChangeModal } from './IrreversiblePlanChangeModal';
import { PlanActionButton } from './PlanActionButton';
import { TaskLimitDropdown } from './TaskLimitDropdown';

type PlanCardProps = {
  currentPlan?: PlanV2;
  /** The type of currency to display. */
  currency: Currency;
  /** True if the user is on a plan outside of their current plan group. */
  isOnLegacyPlan: boolean;
  /** The function that runs when the PlanActionButton is clicked. */
  onSelect: (plan: PlanV2, planChangeType: ChangeType) => void;
  /** The plan to display the information of. */
  planStack: PlanStack;
  /** The plan directly below the `plan` prop. For example, if `plan` is Starter, `previousPlan` is Free. If `plan` is Free, `previousPlan` is undefined. */
  previousPlan?: PlanV2;
  /** A ref that the page scrolls to when the Compare Plans button is clicked. */
  scrollTargetRef: Ref<any>;
  /** The size of the viewport. */
  size: Size;
  /** The direction in which the plan feature tooltips should appear. */
  tooltipPosition?: TooltipPosition;
};

const minPlanCardWidth = 224;
const minPlanCardContentWidth = 184;

const planCardHighlightStyles = {
  background: Colors.white,
  boxShadow: `inset 0 0 0 1px ${Colors.grey200}, ${Shadows.elevation5}`,
};

const Styles = labelStyles(__filename, {
  root: (size: Size) => [
    css`
      display: flex;
      flex-direction: column;
      min-width: ${minPlanCardWidth}px;
    `,
    withModifier(
      size,
      {
        large: {
          width: minPlanCardWidth,
        },
        medium: {},
        small: { display: 'block' },
      }[size]
    ),
  ],

  planFeatures: (size: Size) => [
    css`
      display: inline-block;
      min-width: ${minPlanCardContentWidth}px;
    `,
    withModifier(
      size,
      {
        small: {
          padding: '0 20px',
          width: '100%',
        },
        medium: {
          width: 211,
        },
        large: {
          width: '100%',
        },
      }[size]
    ),
  ],

  planFeaturesWrapper: css`
    display: flex;
    justify-content: center;
  `,

  planOverview: (size: Size, isEligibleForCompanyTrial: boolean) => [
    css`
      display: grid;
      grid-template-rows: 60px 50px 15px auto 60px 10px 40px 10px 50px ${isEligibleForCompanyTrial
          ? '10px 15px'
          : ''}; /* increase the height of the plan cards if there's a link below any of the buttons */
      height: 100%;
      margin: 0 auto;
      min-width: ${minPlanCardContentWidth}px;
    `,
    withModifier(
      size,
      {
        medium: {
          width: minPlanCardContentWidth,
        },
        large: {
          width: minPlanCardContentWidth,
        },
        small: {},
      }[size]
    ),
  ],

  planOverviewWrapper: (
    isCurrentPlan: boolean,
    isEligibleForCompanyTrial: boolean,
    size: Size
  ) => [
    {
      background: Colors.grey000,
      borderRadius: 10,
      height: isEligibleForCompanyTrial ? 415 : 390,
      padding: 18,
      transition: 'all 0.5s ease',
      ':hover': planCardHighlightStyles,
    },
    withModifier(
      size,
      {
        small: {
          display: 'block',
        },
        medium: {},
        large: {},
      }[size]
    ),
    isCurrentPlan && withModifier('isCurrentPlan', planCardHighlightStyles),
  ],
});

/**
 * A component that displays a plan's price, details, and more.
 */
export const PlanCard = ({
  currency,
  currentPlan,
  isOnLegacyPlan,
  onSelect,
  planStack,
  previousPlan,
  scrollTargetRef,
  size,
  tooltipPosition,
}: PlanCardProps) => {
  const { numDiscounts } = useDiscounts();
  const { isSwitchOn: isHardshipReliefModalEnabled } = useAccountSwitch(
    'showHardshipReliefModal'
  );

  const defaultSelectedPlan =
    planStack.plans.find(plan => plan.id === currentPlan?.id) ||
    planStack.plans[0];

  const [selectedPlan, setPlan] = useState(defaultSelectedPlan);

  // Because `planStack.plans` has both yearly and monthly variants of the same plan
  // filter monthly ones out so we don't show duplicates in the dropdown
  const dropdownOptions = planStack.plans.filter(p => isYearly(p.interval));

  // The yearly and monthly variants of the same plan.
  // Used to display both prices in the plan summary
  const compareFields = ['tasks', 'name', 'zaps', 'description'];
  const planVariants = planStack.plans.filter(p =>
    isEqual(pick(p, compareFields), pick(selectedPlan, compareFields))
  );

  const [isShowingCompanyTrialModal, setIsShowingCompanyTrialModal] = useState(
    false
  );

  const [
    isShowingHardshipReliefModal,
    setIsShowingHardshipReliefModal,
  ] = useState(false);

  const [
    isShowingIrreversiblePlanChangeModal,
    setIsShowingIrreversiblePlanChangeModal,
  ] = useState(false);

  const {
    isEligibleForTeamTrial,
    isEligibleForCompanyTrial,
  } = useEligibility();

  const { activeOffer } = useActiveOffer();

  const numSbapDiscounts = numDiscounts('sbap');

  const changeType = getChangePlanType({
    fromPlan: currentPlan,
    toPlan: selectedPlan,
  });

  const shouldShowHardshipReliefModal =
    isHardshipReliefModalEnabled && // Gargoyle switch is turned on
    numSbapDiscounts < 2 && // Haven't recieved a relief extension yet
    !activeOffer && // Don't have an active special offer on their account
    isFree(selectedPlan) && // Are downgrading to a free plan
    changeType === 'downgrade';

  const shouldShowIrreversiblePlanChangeModal = isOnLegacyPlan || activeOffer;
  const isLoggedInPricingPage = !!currentPlan;
  const shouldRenderDemoLink = currentPlan?.type !== 'Company';

  const shouldRenderTrialLink =
    isLoggedInPricingPage &&
    ((isEligibleForCompanyTrial && planStack.planType === 'Company') ||
      (isEligibleForTeamTrial && planStack.planType === 'Team'));

  const shouldRenderActionLink = shouldRenderDemoLink || shouldRenderTrialLink;
  const freeTrialLink = getTrialUrl(planStack.planType as TrialType);

  const ActionLink = () => {
    if (!isLoggedInPricingPage && planStack.planType === 'Company') {
      return (
        <Link href="https://go.zapier.com/companies-demo-request/?demo_source=pricing-logged-out">
          Get a Demo
        </Link>
      );
    }

    if (shouldRenderDemoLink && planStack.planType === 'Company') {
      return (
        <Link href="https://go.zapier.com/companies-demo-request/?demo_source=pricing-logged-in">
          Get a Demo
        </Link>
      );
    }

    if (shouldRenderTrialLink && planStack.planType === 'Team') {
      return <Link href={freeTrialLink}>Start 14-day Trial</Link>;
    }

    return <Spacer height={15} />;
  };

  return (
    <div css={Styles.root(size)} id={`plan-card-${planStack.planType}`}>
      <div
        css={Styles.planOverviewWrapper(
          currentPlan?.id === selectedPlan.id,
          isEligibleForCompanyTrial,
          size
        )}
      >
        <div css={Styles.planOverview(size, isEligibleForCompanyTrial)}>
          <PlanSummary
            currency={currency}
            description={selectedPlan.description}
            planVariants={planVariants}
            title={planStack.planType ? planStack.planType : selectedPlan.name}
          />

          <Spacer height={10} />

          <TaskLimitDropdown
            currency={currency}
            onSelect={item => setPlan(item)}
            options={dropdownOptions}
            selectedPlan={selectedPlan}
          />

          <Spacer height={10} />

          <PlanActionButton
            isUserOnLegacyPlan={isOnLegacyPlan}
            onClick={
              shouldShowHardshipReliefModal
                ? () => setIsShowingHardshipReliefModal(true)
                : shouldShowIrreversiblePlanChangeModal
                ? () => setIsShowingIrreversiblePlanChangeModal(true)
                : onSelect
            }
            planCardPlan={selectedPlan}
            userPlan={currentPlan}
          />

          {shouldRenderActionLink && (
            <Fragment>
              <Spacer height={10} />

              <Text textAlign="center" type="altText800">
                <ActionLink />
              </Text>
            </Fragment>
          )}

          {isShowingCompanyTrialModal && (
            <TrialConfirmationModal
              onClose={() => setIsShowingCompanyTrialModal(false)}
              size={size}
              startHref={freeTrialLink}
            />
          )}
        </div>
      </div>

      <div ref={scrollTargetRef}>
        <Spacer height={35} />
      </div>

      <div css={Styles.planFeaturesWrapper}>
        <div css={Styles.planFeatures(size)}>
          <PlanFeatureList
            features={selectedPlan.features || []}
            plan={selectedPlan}
            previousPlan={previousPlan}
            size={size}
            title={`${planStack.planType} Plan Features`}
            tooltipPosition={tooltipPosition}
          />
        </div>
      </div>

      {isShowingIrreversiblePlanChangeModal && (
        <IrreversiblePlanChangeModal
          irreversibleType={activeOffer ? 'promotional' : 'legacy'}
          onClose={() => setIsShowingIrreversiblePlanChangeModal(false)}
          onContinue={() => onSelect(selectedPlan, changeType)}
        />
      )}

      {isShowingHardshipReliefModal && (
        <HardshipReliefModal
          hasHadSbapDiscountBefore={numSbapDiscounts > 0}
          onClose={() => setIsShowingHardshipReliefModal(false)}
          onContinue={() => onSelect(selectedPlan, changeType)}
        />
      )}
    </div>
  );
};
